import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { isValid, parseISO } from 'date-fns';
import { NumericFormat } from 'react-number-format';
import * as yup from 'yup';

// DS
import {
  Col,
  Form,
  Row,
  VertButton,
  VertInput,
  VertModal,
  VertSelect,
} from '@vertem-core/vertice-ds';

// UTILS
import { calculatePriceForPoint } from 'Utils/calculatePriceForPoint';

import {
  addProduct,
  editProduct,
  loadRedemptionCategories,
  loadRedemptionSubCategories,
  setInitialModalProduct,
  setIsEdit,
  setOpenModal,
} from 'redux/Reducers/Spot/spotNewSolicitationSlice';
import useParticipantSubSegment from 'Hooks/useParticipantSubSegment';

const schema = yup.object().shape({
  nomeProduto: yup.string().required('Campo obrigatório'),
  quantidade: yup
    .string()
    .required('Campo obrigatório')
    .min(1, 'Mínimo 1')
    .matches(/^[0-9]+$/, 'Apenas números'),
  valor: yup.string().required('Campo obrigatório'),
  descricao: yup.string(),
});

// NEW SOLICITATION MODAL
const NewSolicitationModal = ({ cotacaoPremmiar }) => {
  /* Hooks */
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const userProfile = useSelector((state) => state.oidc.user.profile);
  const userInfos = useSelector(({ oidc: data }) => data);
  const {
    showOpen,
    modalProduct,
    isEditModal,
    tableProduct,
    idTemporario,
    dataCategories,
    dataSubCategories,
  } = useSelector((state) => state.SpotNewSolicitation);

  const { isPMO } = useParticipantSubSegment();

  /* States */
  const [valorProduto, setValorProduto] = useState(0);
  const [loading, setLoading] = useState(false);
  const [subCategoriesDisabled, setSubCategoriesDisabled] = useState(true);
  const {
    user: { profile, access_token },
  } = userInfos;
  const username = profile.username || profile['custom:login'];
  const config = {
    campaignId: userProfile['custom:campaign_id'],
    catalogId: userProfile['custom:catalog_id'],
    participantId: userProfile['custom:participant_id'],
    Authorization: `Bearer ${access_token}`,
    username: username,
  };

  const valueCategories = dataCategories.map((category) => ({
    value: category.id,
    label: category.name,
  }));

  const valueSubCategories = dataSubCategories.map((subCategory) => ({
    value: subCategory.id,
    label: subCategory.name,
  }));

  const handleSelectChangeCategory = (value) => {
    form.resetFields(['subCategorias']);

    dispatch(
      loadRedemptionSubCategories({
        id: value,
      })
    );
  };

  const handleSelectChangeSubCategory = (value) => {
    console.log('Subcategory selected:', value);
  };

  /* Handlers */
  const closeModal = () => {
    form.resetFields();
    dispatch(setInitialModalProduct());
    form.setFieldsValue({
      nomeProduto: '',
      quantidade: '',
      valor: '',
      descricao: '',
    });

    if (isEditModal) {
      dispatch(setIsEdit(false));
    }
    setValorProduto(0);
    dispatch(setOpenModal(false));
  };

  const handleFormEditProduct = async (values) => {
    const cleanedValue = values.valor.replace(/R\$\s?|,/g, '').replace('.', '');
    const floatValue = parseFloat(cleanedValue) / 100;
    const unitPrice = await calculatePriceForPoint(floatValue, config);
    const data = {
      ...values,
      valor: floatValue,
      unitPrice,
    };

    setLoading(true);
    dispatch(editProduct(data));

    dispatch(setIsEdit(false));
    form.resetFields();
    form.setFieldsValue({
      nomeProduto: '',
      quantidade: '',
      valor: '',
      descricao: '',
    });
    setValorProduto(0);
    setLoading(false);
    dispatch(setOpenModal(false));
  };

  const handleFormAddProduct = async (values) => {
    setLoading(true);
    const data = {
      ...values,
      valor: valorProduto,
      id: tableProduct.length,
      unitPrice: await calculatePriceForPoint(valorProduto, config),
    };
    dispatch(addProduct(data));

    dispatch(setInitialModalProduct());
    form.resetFields();
    form.setFieldsValue({
      nomeProduto: '',
      quantidade: '',
      valor: '',
      descricao: '',
    });
    setValorProduto(0);
    setLoading(false);
    dispatch(setOpenModal(false));
  };

  /* Utils */
  function isValidDate(value) {
    try {
      const parsedDate = parseISO(value);
      return isValid(parsedDate);
    } catch (error) {
      return false;
    }
  }

  const yupSync = {
    async validator({ field }, value) {
      const isDateField = isValidDate(value);

      if (value.includes('-') && !isDateField) {
        const sanitizedValue = value.replace(/[^\d]/g, '');

        await schema.validateSyncAt(field, { [field]: sanitizedValue });
      } else {
        await schema.validateSyncAt(field, { [field]: value });
      }
    },
  };

  /* Lifecycles */
  useEffect(() => {
    if (isEditModal) {
      form.setFieldsValue(modalProduct);
      setValorProduto(modalProduct.valor);
    }
  }, [isEditModal, modalProduct]);

  useEffect(() => {
    dispatch(loadRedemptionCategories());
  }, []);

  /* Renders */
  return (
    <VertModal
      centered
      open={showOpen}
      onCancel={loading ? () => {} : closeModal}
      onOk={closeModal}
      title={isEditModal ? 'Editar Produto' : 'Adicionar Produto'}
      className="vert-modal-product-spot"
      footer={[
        <div className="vert-modal-product-spot__footer">
          <VertButton
            key="back"
            type="highlight"
            htmlType="submit"
            form="vert-modal-product-spot__form"
            loading={loading}
          >
            {isEditModal ? 'Editar' : 'Adicionar'} Produto
          </VertButton>

          <VertButton
            key="submit"
            type="default"
            onClick={closeModal}
            disabled={loading}
          >
            Fechar
          </VertButton>
        </div>,
      ]}
    >
      <Form
        onFinish={(values) => {
          if (isEditModal) {
            handleFormEditProduct({
              ...values,
              id: idTemporario,
            });
          } else {
            handleFormAddProduct(values);
          }
        }}
        form={form}
        id="vert-modal-product-spot__form"
        initialValues={modalProduct}
      >
        <Row gutter={16} verticalSpacing="regular">
          <Col span={24} className="gutter-row">
            <Form.Item name="nomeProduto" rules={[yupSync]}>
              <VertInput label="Nome do produto" />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="quantidade" rules={[yupSync]}>
              <VertInput label="Quantidade" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="valor" rules={[yupSync]}>
              <div className="vert-input vert-input-number-spot">
                <label className="shrink-label">Valor</label>
                <NumericFormat
                  name="valor"
                  value={valorProduto}
                  prefix={'R$ '}
                  thousandSeparator="."
                  decimalSeparator=","
                  allowNegative={false}
                  decimalScale={2}
                  fixedDecimalScale={true}
                  onValueChange={(values) => {
                    setValorProduto(values.floatValue);
                  }}
                />
              </div>
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16} verticalSpacing="regular">
          <Col span={24} className="gutter-row">
            <Form.Item name="descricao" rules={[yupSync]}>
              <VertInput label="Descrição do Produto" />
            </Form.Item>
          </Col>

          {!cotacaoPremmiar && isPMO && (
            <>
              <Col span={24}>
                <Form.Item
                  name="categorias"
                  rules={[
                    {
                      required: true,
                      message: 'Campo obrigatório',
                    },
                  ]}
                >
                  <VertSelect
                    items={valueCategories}
                    placeholder="Categoria do Produto"
                    onChange={handleSelectChangeCategory}
                    showSearch
                  />
                </Form.Item>
              </Col>

              <Col span={24}>
                <Form.Item
                  name="subCategorias"
                  rules={[
                    {
                      required: true,
                      message: 'Campo obrigatório',
                    },
                  ]}
                >
                  <VertSelect
                    items={valueSubCategories}
                    placeholder="Subcategoria do Produto"
                    onChange={handleSelectChangeSubCategory}
                    disabled={!valueSubCategories.length}
                    showSearch
                  />
                </Form.Item>
              </Col>
            </>
          )}
        </Row>
      </Form>
    </VertModal>
  );
};

export default NewSolicitationModal;
